import { useCallback, useEffect, useState } from "react";

/**
 * A promise plus the three things the UI needs to know about it.
 *
 * Small enough to own rather than take a data-fetching dependency for: this
 * app loads two things on mount and neither is cached, refetched or shared.
 */
export interface Async<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export interface AsyncOptions {
  /**
   * Whether to run at all. False holds the call without touching state, so a
   * fetch can wait for something the user has not chosen yet and the UI does
   * not spend that time claiming to load.
   */
  enabled?: boolean;
}

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[] = [],
  options: AsyncOptions = {},
): Async<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  // Only "loading" if it is going to run. Otherwise the first paint shows a
  // skeleton for a request that was never made.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);

  useEffect(() => {
    if (!enabled) return;

    let live = true;
    setLoading(true);
    setError(null);

    run()
      .then((value) => live && setData(value))
      // Ignore a resolution that arrives after unmount or a newer run: setting
      // state then is both useless and a warning.
      .catch((e: Error) => live && setError(e.message))
      .finally(() => live && setLoading(false));

    return () => {
      live = false;
    };
  }, [run, nonce, enabled]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
